/*
 * GoogleLoginButton — chip "Continuar com Google" (1 clique).
 *
 * DORMANTE por padrão: consulta GET /api/auth/google/status ao montar e só
 * renderiza quando o backend responde {configured:true} (secrets do Google
 * presentes no worker). Sem configuração / erro de rede → renderiza null e
 * a UI fica EXATAMENTE como hoje (só magic link).
 *
 * Click → navegação full-page pro backend (/api/auth/google/start) com
 * anonId (migração do trial v2 server-side) + locale. O retorno cai em
 * /auth/google (ver utils/googleAuthReturn.ts).
 */
import { useEffect, useState } from 'react';
import { buildGoogleStartUrl, fetchGoogleOAuthConfigured } from '../api/googleAuth';
import { getOrCreateAnonId } from '../utils/anonId';

interface GoogleLoginButtonProps {
  /** Idioma da UI — viaja como query param pro backend (analytics) */
  locale: string;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Renderiza o divisor "ou" acima do botão (default true) */
  withDivider?: boolean;
}

/** Logo "G" oficial multicolor (inline — sem asset externo) */
function GoogleGIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

export function GoogleLoginButton({ locale, t, withDivider = true }: GoogleLoginButtonProps) {
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetchGoogleOAuthConfigured().then((value) => {
      if (alive) setConfigured(value);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Backend dormante (sem secrets) ou indisponível → chip não existe.
  if (!configured) return null;

  const handleClick = () => {
    // anonId sempre incluído: o botão só aparece em superfícies anônimas
    // (LoginView / gate do trial). Backend ignora se não há trial a migrar.
    window.location.href = buildGoogleStartUrl(getOrCreateAnonId(), locale);
  };

  return (
    <div style={{ width: '100%' }}>
      {withDivider && (
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '14px 0',
          }}
        >
          <span style={{ flex: 1, height: '1px', background: 'var(--ink-mute)', opacity: 0.4 }} />
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '11px',
              color: 'var(--ink-mute)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {t('googleAuth.divider')}
          </span>
          <span style={{ flex: 1, height: '1px', background: 'var(--ink-mute)', opacity: 0.4 }} />
        </div>
      )}
      <button
        type="button"
        onClick={handleClick}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          padding: '10px 16px',
          background: 'transparent',
          border: '1px solid var(--ink-mute)',
          color: 'var(--ink-light)',
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '0.03em',
          cursor: 'pointer',
          transition: 'border-color 180ms ease-out, box-shadow 180ms ease-out',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--ink-light)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--ink-mute)';
        }}
      >
        <GoogleGIcon />
        {t('googleAuth.button')}
      </button>
    </div>
  );
}
